import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { MailEntity } from '@adornis/mail-client/mail-entity.js';
import { archiveMailsOnImap, type MailMove } from '../server/archiveMailOnImap.js';
import { getCurrentUserID } from './currentUserID.js';

/**
 * Archives one or more mails at once: moves each (that still has an IMAP UID and
 * is not already archived) into the server's archive folder over a single IMAP
 * connection, then marks all requested `mail` documents as archived. The
 * documents are kept so the extraction results stay available; the overview just
 * hides archived mails by default. Returns the number of mails newly archived.
 */
export const archiveMails = registerMutation({
  type: () => Int,
  operationName: 'archiveMails',
  resolve: (mailIDs: string[]) => {
    return async () => {
      const userID = getCurrentUserID();
      if (!userID) throw new Error('Nicht angemeldet');
      if (!mailIDs.length) return 0;

      const mailCollection = await getRawCollection<EntityData<MailEntity>>(MailEntity._collectionName);
      const mails = await mailCollection
        .find({ _id: { $in: mailIDs }, archivedAt: null })
        .toArray();
      if (!mails.length) return 0;

      // Only mails that still carry a UID can be moved on the server; the rest are
      // just flagged as archived locally.
      const moves: MailMove[] = [];
      for (const mail of mails) {
        if (mail.emailID) moves.push({ sourceFolder: mail.mailboxFolder || 'INBOX', uid: mail.emailID });
      }

      const archiveFolder = await archiveMailsOnImap(moves);

      const idsToArchive = mails.map(mail => mail._id);
      await mailCollection.updateMany(
        { _id: { $in: idsToArchive } },
        { $set: { archivedAt: new Date(), mailboxFolder: archiveFolder } },
      );

      return idsToArchive.length;
    };
  },
  params: [{ name: 'mailIDs', type: () => [String] }],
});
